import React from 'react';

/**
 * Row virtualization for the inventory grid.
 *
 * Each room-category/block card is wrapped in a LazyCard. When the card is far
 * from the viewport it renders a height-preserving placeholder (the heavy
 * RateAvailabilityBar/Box subtree is unmounted); when it scrolls near, the real
 * content mounts. This caps the number of live grid cells to roughly the
 * viewport (+buffer) instead of keeping every loaded row mounted.
 *
 * Vertical scrolling on this page is the window itself, so the Intersection
 * observer uses the default (viewport) root. Heterogeneous/dynamic row heights
 * (channel and pax collapses) are handled by measuring the real height while the
 * content is mounted and reusing it for the placeholder.
 */
export default class LazyCard extends React.Component {
    constructor(props) {
        super(props);
        this.state = { visible: false, height: props.estimatedHeight || 120 };
        this.ref = React.createRef();
    }

    componentDidMount() {
        if (typeof IntersectionObserver === 'undefined') {
            // Fallback: no virtualization support, always render content.
            this.setState({ visible: true });
            return;
        }
        this.observer = new IntersectionObserver(this.handleIntersect, { rootMargin: '1200px 0px' });
        if (this.ref.current) {
            this.observer.observe(this.ref.current);
        }
    }

    componentWillUnmount() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }

    handleIntersect = (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
            if (!this.state.visible) {
                this.setState({ visible: true });
            }
        } else if (this.state.visible) {
            // Capture the real height before unmounting so the placeholder keeps
            // the scroll geometry stable.
            const measured = this.ref.current ? this.ref.current.getBoundingClientRect().height : 0;
            this.setState(prev => ({ visible: false, height: measured > 0 ? measured : prev.height }));
        }
    }

    render() {
        const { visible, height } = this.state;
        return (
            <div ref={this.ref} style={visible ? undefined : { height }}>
                {visible ? this.props.children : null}
            </div>
        );
    }
}
